import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, delay, retry, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IPrintJob } from '../print-job/models/print-job.model';
import { IPrinter } from '../printer/models/printer.model';
import { IQueue } from '../printer/models/queue.model';
import { ReleaseResourceService } from '../release-resource/release-resource.service';
import { StringularPipe } from '../../pipes/stringular/stringular.pipe';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class ReleasePrintService {

  private unSubscribe = new Subject<void>();
  public releaseResource: any = null;
  private logger: Logger;

  constructor(
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    public releaseResourceService: ReleaseResourceService,
    private stringularPipe: StringularPipe,
    private toastController: ToastController,
    private translateService: TranslateService,
    public userService: UserService,
  ) {
    this.logger = this.loggingService.getLogger("[ReleasePrintService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public releaseSelectedPrintJobs(
    selectedPrinter: IPrinter,
    selectedPrintJobs: Array<IPrintJob>,
    selectedQueue: IQueue,
    startReleaseUrl: string
  ): Observable<Array<IPrintJob>> {
    const methodName = 'releaseSelectedPrintJobs() ';
    this.logger.info(methodName);

    if (!selectedPrinter) {
      throw new Error("PrintJobListService.releaseSelectedPrintJobs: printer is missing or it has errors");
    }

    if (!selectedPrintJobs || selectedPrintJobs.length === 0) {
      throw new Error("PrintJobListService.releaseSelectedPrintJobs: no selected print jobs");
    }

    return this.httpClient.post(startReleaseUrl, {}).pipe(
      // Starting a release is not idempotent: re-POSTing a start the server already acted on comes
      // back as 400 STARTING_RELEASE_INVALID_STATE, which hides the real first failure.
      retry({
        count: 2,
        delay: (error: HttpErrorResponse) => {
          if (!this.isTransientStartFailure(error)) {
            this.logger.info(methodName + 'not retrying start - '
              + JSON.stringify({ status: error.status, errorCode: error.error?.errorCode ?? 'none' }));
            return throwError(() => error);
          }
          this.logger.info(methodName + 'transient start failure, retrying in 1000 ms - '
            + JSON.stringify({ status: error.status }));
          return of(error).pipe(delay(1000));
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const err = new HttpErrorResponse(error);
        return  throwError(() => err);
      }),
      switchMap((response: any) => {
        this.logger.info(methodName + '- release started without errors');
        let message: string;
        const selectedJobCount = selectedPrintJobs.length;

        if (selectedJobCount > 1) {
          message = this.translateMessageWithParameter('PrintDocs', [selectedJobCount.toString()]);
        } else if (selectedJobCount === 1) {
          message = this.translateMessageWithParameter('PrintDoc');
        }

        this.showToastForReleasePrint(message);

        if (selectedQueue !== null) {
          this.logger.info(methodName + '- setting lastUsedPrinter: ' + selectedPrinter.links.self);
          return this.userService.setLastUsedPrinter(selectedPrinter).pipe(
            takeUntil(this.unSubscribe),
            switchMap(() => {
              this.logger.info(methodName + '- setting lastUsedQueue: ' + selectedQueue.links.self);
              return this.userService.setLastUsedQueue(selectedQueue).pipe(
                takeUntil(this.unSubscribe),
                tap(() => {
                  this.unSubscribe.next();
                  this.unSubscribe.complete();
                }),
                switchMap(() => of(response))
              );
            })
          );
        } else {
          this.unSubscribe.next();
          this.unSubscribe.complete();
          return of(response);
        }
      })
    );
  }

  // Status 0 is a connection/CORS level failure that never reached the endpoint; 5xx may succeed on
  // a second attempt. Every 4xx is a rejection of the request itself and will be rejected again.
  private isTransientStartFailure(error: HttpErrorResponse): boolean {
    return error.status === 0 || error.status >= 500;
  }

 private translateMessageWithParameter(text: string, args?: string[]): string {
  this.logger.info('translateMessageWithParameter()');
  if (text) {
    this.translateService.get(text)
    .pipe(takeUntil(this.unSubscribe))
    .subscribe((value: string) => {
      text = value;
      this.unSubscribe.next();
      this.unSubscribe.complete();
    });
    if (args) {
      text = this.stringularPipe.transform(text, ...args);
    }
    return text;
  }
}

private showToastForReleasePrint(message): void {
  this.logger.info('showToastForReleasePrint()');
  const toast = this.toastController.create({
    message: message,
    duration: 3000,
    position: 'top'
  }).then(toast => toast.present());
}
}
